import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { RadioChangeEvent } from "antd";
import { Breadcrumb, Button, Input, message, Radio, Select, Spin } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useRouter } from "next/router";
import Link from "next/link";
import styles from "@aspen/theme/Wallet.module.less";
import {
  convertUSD2USDC,
  GA_EVENT_NAME,
  hasPermissionOrNotSubAccount,
  i18nUtil,
  kycCountrySelectOptionList,
  reportEvent,
  WALLET_PATHS,
  PORTFOLIO_PATHS,
  getCountryDisplayName,
  MEMO_COINS,
  scientificToNumber,
} from "@aspen/libs";
import {
  addWhitelistAddress,
  checkWithdrawAddressFormat,
  getAssetCurrencyConfig,
  getDepositAddress,
  getTravelRuleVasp,
} from "@aspen/services";
import type {
  IBusinessVerificationHeaderModel,
  ICurrencyAsset,
  ICurrencyConfig,
  IReqSecurityVerifyHeader,
  IVaspType,
} from "@aspen/model";
import {
  IBindingBusinessType,
  ICustomerType,
  ISubAccountAuthType,
  EWhitelistAddressSource,
  WALLET_WHITELIST_TAB_TYPE,
} from "@aspen/model";

import { ModalBusinessVerification, ModalResult, WithRiskReminder } from "@aspen/widgets";
import { userCountriesData } from "@aspen/assets/offlineData/usersCountries";
import { CommonSelect } from "@adgm/cross-ui";

const { Option } = Select;

/**
 * 根据最小入金生成「所有权验证」金额（UI 占位逻辑，后续可与接口对齐）。
 * - 最小入金 ≥ 1000：在最小入金上叠加随机整数 1–9；
 * - 否则：在末位小数后再扩展一位随机数字 1–9（如 0.01 → 0.01X）。
 *
 * @param minDeposit - 链上最小入金数量
 * @param showPrecision - 资产展示精度
 * @returns 验证金额字符串
 */
const computeOwnershipTestAmount = (minDeposit: number, showPrecision: number): string => {
  const min = Number.isFinite(minDeposit) && minDeposit >= 0 ? minDeposit : 0;
  const randomDigit = Math.floor(Math.random() * 9) + 1;
  const precisionCap = showPrecision > 0 ? showPrecision : 8;

  if (min >= 1000) {
    return String(min + randomDigit);
  }

  const normalized = scientificToNumber(min);
  const baseStr = typeof normalized === "number" ? normalized.toString() : String(normalized);
  const fractionDigits = baseStr.includes(".") ? baseStr.split(".")[1].length : 0;
  const addedScale = fractionDigits + 1;
  const increment = Math.pow(10, -addedScale) * randomDigit;
  const sum = min + increment;
  const decimals = Math.min(addedScale, precisionCap);
  return String(Number(sum.toFixed(decimals)));
};

const ownershipReadonlyBoxStyle: React.CSSProperties = {
  backgroundColor: "var(--main-bg-color)",
  padding: "10px 12px",
  color: "var(--white-op2)",
  minHeight: 40,
};

const ownershipReadonlyCopyBoxStyle: React.CSSProperties = {
  ...ownershipReadonlyBoxStyle,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const AddAddress = () => {
  const intl = i18nUtil.t();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(true);
  const [coinList, setCoinList] = useState<ICurrencyConfig[]>([]);
  const [chainList, setChainList] = useState<ICurrencyAsset[]>([]);
  const [currentCoinInfo, setCurrentCoinInfo] = useState<ICurrencyConfig>();
  const [currentChainInfo, setCurrentChainInfo] = useState<ICurrencyAsset>();
  const [addressInfo, setAddressInfo] = useState<string>("");
  const [xrpTag, setXrpTag] = useState<string>("");
  const [addressFormat, setAddressFormat] = useState<boolean>(true);
  const [noteList, setNoteList] = useState<IVaspType[]>([]);
  const [type, setType] = useState<ICustomerType>(ICustomerType.INDIVIDUAL);
  const [addSuccessModalVisible, setAddSuccessModalVisible] = useState<boolean>(false);
  const [showBusinessVerification, setShowBusinessVerification] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [location, setLocation] = useState<string[]>([]);
  const [sourceAddressType, setSourceAddressType] = useState<EWhitelistAddressSource>(
    EWhitelistAddressSource.CUSTODIAN,
  );
  const [sourceAddressValue, setSourceAddressValue] = useState<IVaspType | undefined>();
  const [ownershipTestAmount, setOwnershipTestAmount] = useState<string>("");
  const [ownershipDepositAddress, setOwnershipDepositAddress] = useState<string>("");
  const [ownershipDepositMemo, setOwnershipDepositMemo] = useState<string>("");

  const handleCopySuccess = useCallback(() => {
    message.success(i18nUtil.t()["copied"]);
  }, []);

  const fetchOwnershipDepositInfo = (asset: string) => {
    if (!asset) {
      setOwnershipDepositAddress("");
      setOwnershipDepositMemo("");
      return;
    }
    getDepositAddress({ currency: asset, required: true })
      .then(res => {
        if (res?.code === "0") {
          setOwnershipDepositAddress(res.data?.address ?? "");
          setOwnershipDepositMemo(res.data?.memo ?? "");
        } else {
          setOwnershipDepositAddress("");
          setOwnershipDepositMemo("");
          message.error(intl[res?.msg] ?? res?.msg);
        }
      })
      .catch(() => {
        setOwnershipDepositAddress("");
        setOwnershipDepositMemo("");
      });
  };

  useEffect(() => {
    if (!currentChainInfo) {
      return;
    }
    setOwnershipTestAmount(
      computeOwnershipTestAmount(
        currentChainInfo.minDepositAmount ?? 0,
        currentChainInfo.showPrecision ?? 8,
      ),
    );
    fetchOwnershipDepositInfo(currentChainInfo.asset ?? "");
  }, [
    currentChainInfo?.asset,
    currentChainInfo?.chainName,
    currentChainInfo?.minDepositAmount,
    currentChainInfo?.showPrecision,
  ]);

  const successMapData = useMemo(() => {
    const rows: { label: string; value: React.ReactNode }[] = [
      {
        label: intl["wallet.withdraw.white.history.denomination"],
        value: convertUSD2USDC(currentCoinInfo?.currency ?? ""),
      },
      {
        label: intl["wallet.withdraw.white.history.network"],
        value: currentChainInfo?.chainName,
      },
    ];
    rows.push(
      {
        label:
          currentChainInfo?.asset === "XRP"
            ? intl["wallet.withdraw.white.add.tag"]
            : intl["wallet.deposit.hbar.memo"],
        value: xrpTag,
      },
      {
        label: intl["wallet.withdraw.white.add.wallet.address"],
        value: addressInfo,
      },
      {
        label: intl["wallet.withdraw.white.add.source.name"],
        value: sourceAddressValue?.name || "--",
      },
      {
        label: intl["wallet.withdraw.white.add.country.of.deposit"],
        value: getCountryDisplayName(country),
      },
      {
        label: intl["wallet.withdraw.white.add.originator"],
        value: type,
      },
    );
    return rows;
  }, [
    intl,
    currentCoinInfo?.currency,
    currentChainInfo?.asset,
    currentChainInfo?.chainName,
    sourceAddressType,
    ownershipTestAmount,
    ownershipDepositAddress,
    ownershipDepositMemo,
    xrpTag,
    addressInfo,
    sourceAddressValue?.name,
    country,
    type,
  ]);

  useEffect(() => {
    initData();
  }, []);
  useEffect(() => {
    if (currentCoinInfo) {
      checkAddress();
    }
  }, [currentCoinInfo]);

  const initData = () => {
    getAssetCurrencyConfig({})
      .then(res => {
        if (res?.code == "0") {
          const list = res?.data ?? [];
          setCoinList(list);
          if (list.length) {
            setCurrentCoinInfo(list[0]);
            setChainList(list[0]?.assets);
            setCurrentChainInfo(list[0]?.assets[0]);
          }
        }
      })
      .then(() => {
        fetchNoteList("");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleChangeCoin = (currency: string) => {
    const selectInfo = coinList.filter(e => e.currency === currency)[0];
    setCurrentCoinInfo(selectInfo);
    setXrpTag("");
    setChainList(selectInfo?.assets);
    setCurrentChainInfo(selectInfo?.assets[0]);
  };
  const handleChangeChain = (chainName: string) => {
    const selectInfo = chainList.filter(e => e.chainName === chainName)[0];
    setCurrentChainInfo(selectInfo);
  };

  const handleChangeAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressInfo(e.target.value);
  };
  const handleChangeXrpTag = (e: React.ChangeEvent<HTMLInputElement>) => {
    setXrpTag(e.target.value);
  };

  const checkAddress = () => {
    if (addressInfo === "") {
      setAddressFormat(true);
      return;
    }
    if (
      currentChainInfo &&
      (currentChainInfo.asset === "XRP" || MEMO_COINS.includes(currentChainInfo.asset ?? "")) &&
      (addressInfo === "" || xrpTag === "")
    )
      return true;
    const params = {
      asset: currentChainInfo?.asset ?? "",
      address: addressInfo,
      memo: xrpTag,
    };
    checkWithdrawAddressFormat(params).then(res => {
      if (res?.code == "0") {
        if (res.data === true) {
          setAddressFormat(true);
        } else {
          setAddressFormat(false);
        }
      }
    });
  };

  const fetchAddAddress = (verifyParam: IBusinessVerificationHeaderModel) => {
    setLoading(true);
    const countryInfo = userCountriesData?.find(item => item.id === country);
    const ownershipAmount = Number(ownershipTestAmount);
    const params = {
      asset: currentChainInfo?.asset ?? "",
      chainName: currentChainInfo?.chainName ?? "",
      address: addressInfo,
      memo: xrpTag,
      smallTransactionTest: {
        asset: currentChainInfo?.asset ?? "",
        address: ownershipDepositAddress,
        amount: Number.isFinite(ownershipAmount) ? ownershipAmount : 0,
        network: currentChainInfo?.chainName ?? "",
        memo: ownershipDepositMemo,
      },
      customerType: type.toUpperCase(),
      firstName,
      lastName,
      companyName,
      location: JSON.stringify(location),
      vaspDid: sourceAddressValue?.did || "",
      vaspName: sourceAddressValue?.name || "",
      addressSource: sourceAddressType,
      ...(countryInfo?.id
        ? {
            country: JSON.stringify({
              id: countryInfo?.id,
              nationality: countryInfo?.nationality,
              commonName: countryInfo?.commonName,
            }),
          }
        : {}),
    };
    let headerParams: IReqSecurityVerifyHeader = {
      businessType: IBindingBusinessType.whitelistAddAddress,
      smsType: 0,
    };
    headerParams = { ...headerParams, ...verifyParam };
    const reportParams = {
      ...params,
      smallTransactionTest: JSON.stringify(params.smallTransactionTest),
    };
    reportEvent({
      moduleName: GA_EVENT_NAME.whithdraw.addAddress,
      detailParams: reportParams,
    });
    addWhitelistAddress(params, headerParams)
      .then(res => {
        if (res?.code == "0") {
          setShowBusinessVerification(false);
          setAddSuccessModalVisible(true);
        } else {
          message.error(intl[res.msg] ?? res?.msg);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchNoteList = (namePrefix: string) => {
    setNoteList([]);
    getTravelRuleVasp(namePrefix).then(res => {
      if (res?.code === "0") {
        setNoteList(res?.data);
      }
    });
  };
  const handleChangeCustomerType = (customerType: ICustomerType) => {
    if (type === customerType) {
      return;
    }
    setType(customerType);
  };
  const saveStatus = () => {
    const xrpStatus =
      (currentChainInfo?.asset !== "XRP" && !MEMO_COINS.includes(currentChainInfo?.asset ?? "")) ||
      xrpTag;
    const individualStatus =
      type === ICustomerType.INDIVIDUAL && !!firstName && !!lastName && !!country;
    const institutionStatus = type === ICustomerType.INSTITUTION && !!companyName && !!country;
    const sourceAddressStatus = !!sourceAddressValue;
    return (
      addressInfo &&
      addressFormat &&
      (individualStatus || institutionStatus) &&
      xrpStatus &&
      sourceAddressStatus
    );
  };

  return (
    <Spin spinning={loading}>
      <Breadcrumb style={{ marginBottom: "24px" }}>
        <Breadcrumb.Item>{intl["kyc.certify.location"]}:</Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link href={PORTFOLIO_PATHS.PORTFOLIO_WALLET}>{intl["wallet"]}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{intl["wallet.withdraw.select.address.btn.add"]}</Breadcrumb.Item>
      </Breadcrumb>
      <div className={styles.addNewAddressContent}>
        <p className={styles.title}>{intl["wallet.withdraw.white.add.title"]}</p>
        <p className={styles.groupTitle}>
          {intl["wallet.withdraw.white.list.input.label.title.address.info"]}
        </p>
        <div className={styles.selectView}>
          <div className={styles.selectCoinView}>
            <p className={styles.label}>{intl["wallet.withdraw.white.history.denomination"]}</p>
            <Select
              className={styles.selectCoin}
              value={currentCoinInfo?.currency}
              style={{ width: 160 }}
              onChange={e => handleChangeCoin(e)}>
              {coinList.map((item, index) => {
                return (
                  <Option key={index} value={item.currency}>
                    {convertUSD2USDC(item.currency)}
                  </Option>
                );
              })}
            </Select>
          </div>
          <div className={styles.selectCoinView}>
            <p className={styles.label}>{intl["wallet.withdraw.white.history.network"]}</p>
            <Select
              className={styles.selectCoin}
              value={currentChainInfo?.chainName}
              style={{ width: 160 }}
              onChange={e => handleChangeChain(e)}>
              {chainList.map((item, index) => {
                return (
                  <Option key={index} value={item.chainName}>
                    {item?.chainName}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
        <div className={styles.itemView}>
          <p className={styles.label}>
            {intl["wallet.withdraw.white.add.wallet.address"]}
            <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
          </p>
          <Input
            bordered={false}
            onChange={handleChangeAddress}
            placeholder={intl["placeholder.enter"]}
            onBlur={checkAddress}
            value={addressInfo}
          />
          {!addressFormat && (
            <div className={styles.errorTips}>
              <p>{intl["wallet.withdraw.white.address.error"]}</p>
            </div>
          )}
        </div>
        {(currentChainInfo?.asset === "XRP" ||
          MEMO_COINS.includes(currentChainInfo?.asset ?? "")) && (
          <div className={styles.itemView}>
            <p className={styles.label}>
              {currentChainInfo?.asset === "XRP"
                ? intl["wallet.withdraw.white.add.tag"]
                : intl["wallet.deposit.hbar.memo"]}
            </p>
            <Input
              bordered={false}
              onChange={handleChangeXrpTag}
              placeholder={intl["placeholder.enter"]}
              onBlur={checkAddress}
              value={xrpTag}
            />
          </div>
        )}
        <div className={styles.itemView}>
          <p className={styles.label}>{intl["wallet.withdraw.white.add.source.address"]}</p>
          <Radio.Group
            value={sourceAddressType}
            onChange={(e: RadioChangeEvent) => setSourceAddressType(e.target.value)}
            style={{ marginBottom: "16px" }}>
            <Radio value={EWhitelistAddressSource.CUSTODIAN}>
              {intl["wallet.withdraw.white.add.source.address.exchange"]}
            </Radio>
            <Radio value={EWhitelistAddressSource.OTHERS}>
              {intl["wallet.withdraw.white.add.source.address.selfhost"]}
            </Radio>
          </Radio.Group>
          <p className={styles.label}>
            {intl["wallet.withdraw.white.add.source.name"]}
            <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
          </p>
          {sourceAddressType === EWhitelistAddressSource.CUSTODIAN ? (
            <Select
              showSearch
              allowClear
              size="large"
              style={{ width: "100%" }}
              onClear={() => fetchNoteList("")}
              onSearch={fetchNoteList}
              onChange={e => {
                const selectedNote = noteList.find(item => item.did === e);
                setSourceAddressValue(selectedNote);
              }}
              placeholder={intl["placeholder.enter.to.select"]}
              optionFilterProp="label"
              options={noteList?.map(d => ({
                value: d.did,
                label: d.name,
              }))}
            />
          ) : (
            <Input
              bordered={false}
              onChange={e => setSourceAddressValue({ name: e.target.value, did: "" })}
              placeholder={intl["wallet.withdraw.white.add.address.placeholder"]}
              value={sourceAddressValue?.name}
            />
          )}
        </div>
        <div className={styles.itemView}>
          <p className={styles.label}>
            {intl["wallet.withdraw.white.add.country.of.deposit"]}
            <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
          </p>
          <CommonSelect
            showSearch
            allowClear
            style={{ width: "100%", height: "100%" }}
            placeholder={intl["placeholder.select"]}
            options={kycCountrySelectOptionList("commonName")}
            onSelect={e => setCountry(e)}
          />
        </div>
        <p className={styles.groupTitle}>{intl["wallet.withdraw.white.add.personal.info"]}</p>
        <div className={styles.itemView}>
          <Radio.Group
            value={type}
            onChange={(e: RadioChangeEvent) => handleChangeCustomerType(e.target.value)}>
            <Radio value={ICustomerType.INDIVIDUAL}>{intl["adgm.wallet.radio.individual"]}</Radio>
            <Radio value={ICustomerType.INSTITUTION}>{intl["adgm.wallet.radio.institution"]}</Radio>
          </Radio.Group>
        </div>
        {type === ICustomerType.INDIVIDUAL && (
          <div className={styles.containerView}>
            <div className={styles.itemView}>
              <p className={styles.label}>
                {intl["adgm.wallet.input.label.first.name"]}
                <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
              </p>
              <Input
                bordered={false}
                onChange={e => setFirstName(e.target.value)}
                placeholder={intl["placeholder.enter"]}
                value={firstName}
              />
            </div>
            <div className={styles.itemView}>
              <p className={styles.label}>
                {intl["adgm.wallet.input.label.last.name"]}
                <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
              </p>
              <Input
                bordered={false}
                onChange={e => setLastName(e.target.value)}
                placeholder={intl["placeholder.enter"]}
                value={lastName}
              />
            </div>
          </div>
        )}
        {type === ICustomerType.INSTITUTION && (
          <div className={styles.itemView}>
            <p className={styles.label}>
              {intl["adgm.wallet.input.label.company.name"]}
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            </p>
            <Input
              bordered={false}
              onChange={e => setCompanyName(e.target.value)}
              placeholder={intl["placeholder.enter"]}
              value={companyName}
            />
          </div>
        )}
        <div className={styles.containerView}>
          <div className={styles.itemView}>
            <p className={styles.label}>{intl["adgm.wallet.detail.address.line1"]}</p>
            <Input
              bordered={false}
              onChange={e => {
                const newLocation = [...location];
                newLocation[0] = e.target.value;
                setLocation(newLocation);
              }}
              placeholder={intl["placeholder.enter"]}
              value={location[0]}
            />
          </div>
          <div className={styles.itemView}>
            <p className={styles.label}>{intl["adgm.wallet.detail.address.line2"]}</p>
            <Input
              bordered={false}
              onChange={e => {
                const newLocation = [...location];
                newLocation[1] = e.target.value;
                setLocation(newLocation);
              }}
              placeholder={intl["placeholder.enter"]}
              value={location[1]}
            />
          </div>
        </div>
        {sourceAddressType === EWhitelistAddressSource.OTHERS && currentChainInfo ? (
          <>
            <p className={styles.groupTitle} style={{ marginBottom: 4 }}>
              {intl["wallet.withdraw.white.add.ownership.test.title"]}
            </p>
            <p
              style={{
                color: "var(--white-op2)",
                marginBottom: 16,
                lineHeight: "22px",
              }}>
              {intl["wallet.withdraw.white.add.ownership.test.instruction"]}
            </p>
            <p className={styles.groupTitle} style={{ marginBottom: 8 }}>
              {intl["wallet.withdraw.white.add.ownership.test.deposit.section"]}
            </p>
            <div className={styles.containerView}>
              <div className={styles.itemView}>
                <p className={styles.label}>{intl["wallet.withdraw.white.history.denomination"]}</p>
                <div style={ownershipReadonlyBoxStyle}>
                  {convertUSD2USDC(currentCoinInfo?.currency ?? "")}
                </div>
              </div>
              <div className={styles.itemView}>
                <p className={styles.label}>{intl["wallet.withdraw.white.history.network"]}</p>
                <div style={ownershipReadonlyBoxStyle}>{currentChainInfo.chainName}</div>
              </div>
            </div>
            <div className={styles.containerView}>
              <div className={styles.itemView}>
                <p className={styles.label}>
                  {intl["wallet.withdraw.white.add.ownership.test.verification.amount"]}
                </p>
                <div style={ownershipReadonlyCopyBoxStyle}>
                  <span style={{ flex: 1, wordBreak: "break-all" }}>{ownershipTestAmount}</span>
                  <CopyToClipboard text={ownershipTestAmount} onCopy={handleCopySuccess}>
                    <CopyOutlined
                      style={{ color: "var(--white-op2)", fontSize: 12, cursor: "pointer" }}
                    />
                  </CopyToClipboard>
                </div>
              </div>
              <div className={styles.itemView}>
                <p className={styles.label}>
                  {intl["wallet.withdraw.white.add.ownership.test.deposit.address"]}
                </p>
                <div style={ownershipReadonlyCopyBoxStyle}>
                  <span style={{ flex: 1, wordBreak: "break-all" }}>{ownershipDepositAddress}</span>
                  <CopyToClipboard text={ownershipDepositAddress} onCopy={handleCopySuccess}>
                    <CopyOutlined
                      style={{ color: "var(--white-op2)", fontSize: 12, cursor: "pointer" }}
                    />
                  </CopyToClipboard>
                </div>
              </div>
            </div>
            {(currentCoinInfo?.currency === "XRP" ||
              MEMO_COINS.includes(currentCoinInfo?.currency ?? "")) &&
            ownershipDepositMemo?.trim() ? (
              <div className={styles.containerView}>
                <div className={styles.itemView}>
                  <p className={styles.label}>
                    {currentCoinInfo?.currency === "XRP"
                      ? intl["wallet.withdraw.white.add.tag"]
                      : intl["wallet.withdraw.white.add.ownership.test.memo.label"]}
                  </p>
                  <div style={ownershipReadonlyCopyBoxStyle}>
                    <span style={{ flex: 1, wordBreak: "break-all" }}>{ownershipDepositMemo}</span>
                    <CopyToClipboard text={ownershipDepositMemo} onCopy={handleCopySuccess}>
                      <CopyOutlined
                        style={{ color: "var(--white-op2)", fontSize: 12, cursor: "pointer" }}
                      />
                    </CopyToClipboard>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {hasPermissionOrNotSubAccount(ISubAccountAuthType.WHITELIST) ? (
          <Button
            type="primary"
            style={{ width: 170 }}
            disabled={!saveStatus()}
            onClick={() => {
              setShowBusinessVerification(true);
            }}>
            {intl["wallet.withdraw.white.add.save"]}
          </Button>
        ) : null}
      </div>
      {showBusinessVerification ? (
        <ModalBusinessVerification
          confirmLoading={loading}
          visible={showBusinessVerification}
          businessType={IBindingBusinessType.whitelistAddAddress}
          confirm={res => {
            fetchAddAddress(res);
          }}
          cancel={() => {
            setShowBusinessVerification(false);
          }}
        />
      ) : null}
      {addSuccessModalVisible ? (
        <ModalResult
          open={addSuccessModalVisible}
          resultType="success"
          onOk={() => {
            setAddSuccessModalVisible(false);
            setLoading(true);
            router.push({
              pathname: WALLET_PATHS.WALLET_WITHDRAW_WHITELIST_MANAGEMENT,
              query: {
                tab: WALLET_WHITELIST_TAB_TYPE.InReview,
              },
            });
          }}
          onCancel={() => {
            router.back();
          }}
          okText={intl["wallet.withdraw.white.add.success.btn.whitelist"]}
          title={intl["wallet.withdraw.white.add.success.title"]}
          text={
            sourceAddressType === EWhitelistAddressSource.OTHERS ? (
              <>
                <span style={{ color: "var(--white)", fontFamily: "var(--CommonMedium)" }}>
                  {intl["wallet.withdraw.white.add.success.finish.ownership.test"]}
                </span>
                <br />
                {intl["wallet.withdraw.white.add.success.content"]}
              </>
            ) : (
              intl["wallet.withdraw.white.add.success.content"]
            )
          }
          list={successMapData}
          maskClosable={false}
        />
      ) : null}
    </Spin>
  );
};

export const PageAdgmWhitelistAddAddress = WithRiskReminder(AddAddress);
